import { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './driver.css';

export const TutorialContext = createContext(null);

const TUTORIALS = {
  'budget-tracker': {
    steps: [
      {
        element: 'h1',
        popover: {
          title: 'Budget Tracker',
          description: 'This is where you track your wedding budget. Based on your onboarding answers, we\'ve given you an example breakdown for your budget. Adjust the numbers along your planning journey to stay on track.',
          side: 'bottom',
          align: 'start',
        },
      },
   
    ],
  },
  'invite-party-member': {
    steps: [
      {
        element: '[data-cy="invite-modal-content"]',
        popover: {
          title: 'Invite Party Members',
          description: 'Use this modal to invite guests to your wedding party.',
          side: 'left',
          align: 'center',
        },
      },
      {
        element: '[data-cy="email-input-0"]',
        popover: {
          title: 'Email Address',
          description: 'Enter the email address of the person you want to invite.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="role-select-0"]',
        popover: {
          title: 'Guest Role',
          description: 'Select a role for the guest you\'re inviting.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="wedding-party-checkbox-0"]',
        popover: {
          title: 'Wedding Party',
          description: 'Check this if the guest should be part of the wedding party.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="add-row-button"]',
        popover: {
          title: 'Add More Guests',
          description: 'Click here to add more guests to your invitation.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-cy="send-invites-button"]',
        popover: {
          title: 'Send Invitations',
          description: 'Click here to send the invitations to all added guests.',
          side: 'top',
          align: 'center',
        },
      },
    ],
  },
  'vendors-list': {
    steps: [
      {
        element: '[data-cy="vendor-search-input"]',
        popover: {
          title: 'Search Vendors',
          description: 'Type your search query here to find specific vendors.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '.MuiTabs-list',
        popover: {
          title: 'List and Grid View',
          description: 'Click here to view vendors in a list or grid layout.',
          side: 'left',
          align: 'center',
        },
      },
      {
        element: '.venderListStyle_heading__gA5C7',
        popover: {
          title: 'Filter Options',
          description: 'Use these filters to narrow down vendors by type, location, and other criteria.',
          side: 'right',
          align: 'start',
        },
      },
    ],
  },
  'venues-list': {
    steps: [
      {
        element: '[data-cy="vendor-search-input"]',
        popover: {
          title: 'Search Venues',
          description: 'Type your search query here to find specific venues.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '.MuiTabs-list',
        popover: {
          title: 'List and Grid View',
          description: 'Click here to view venues in a list or grid layout.',
          side: 'left',
          align: 'center',
        },
      },
      {
        element: '.venderListStyle_heading__gA5C7',
        popover: {
          title: 'Filter Options',
          description: 'Use these filters to narrow down venues by type, location, and other criteria.',
          side: 'right',
          align: 'start',
        },
      },
    ],
  },
  'new-wedding': {
    steps: [
      {
        element: '#new-wedding-btn',
        popover: {
          title: 'Start Planning Your Wedding',
          description: 'Click on "New Wedding" to begin planning your special day!',
          side: 'left',
          align: 'start',
        },
      },
    ],
  },
  'wedding-dashboard': {
    steps: [
      {
        element: '[data-cy="invite"]',
        popover: {
          title: 'Invite Your Partner',
          description: 'Collaborate with your partner by inviting them to plan the wedding together!',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="tasks"]',
        popover: {
          title: 'Manage Tasks',
          description: 'Keep track of all your wedding planning tasks and deadlines in one place.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="budget"]',
        popover: {
          title: 'Track Your Budget',
          description: 'Monitor your wedding expenses and stay within your budget.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="timeline"]',
        popover: {
          title: 'Wedding Timeline',
          description: 'View and manage your wedding day schedule and important milestones.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="guests"]',
        popover: {
          title: 'Guest Management',
          description: 'Manage your guest list, RSVPs, and seating arrangements.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="vendors"]',
        popover: {
          title: 'Vendor Tracker',
          description: 'Keep track of all your wedding vendors, contracts, and communications.',
          side: 'right',
          align: 'start',
        },
      },
    ],
  },
  'timeline': {
    steps: [
      {
        element: '[data-cy="schedule-view"]',
        popover: {
          title: 'Schedule',
          description: 'View and manage your wedding day schedule. See all your events and appointments in one place.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-cy="new-event-button"]',
        popover: {
          title: 'New Event',
          description: 'Click here to add a new event to your wedding schedule.',
          side: 'left',
          align: 'center',
        },
      },
    ],
  },
  'tasks': {
    steps: [
      {
        element: 'h1',
        popover: {
          title: 'Tasks',
          description: 'Manage all your wedding planning tasks here. Create, assign, and track the progress of tasks to stay organized.',
          side: 'bottom',
          align: 'start',
        },
      },
    ],
  },
  'invite-partner': {
    steps: [
      {
        element: 'h1',
        popover: {
          title: 'Invite Your Partner',
          description: 'Collaborate with your partner on wedding planning. Send them an invitation to join and plan together.',
          side: 'bottom',
          align: 'start',
        },
      },
    ],
  },
  'vendor-tracker': {
    steps: [
      {
        element: 'h1',
        popover: {
          title: 'Vendor Tracker',
          description: 'Keep track of all your wedding vendors, proposals, and communications in one place.',
          side: 'bottom',
          align: 'start',
        },
      },
    ],
  },
  'wedding-guests': {
    steps: [
      {
        element: '[data-cy="add-guest-btn"]',
        popover: {
          title: 'Invite Your Guests',
          description: 'Start by inviting guests to your wedding. You can add them one by one or import a list.',
          side: 'bottom',
          align: 'start',
        },
      },
      // {
      //   element: '[data-cy="guest-list"]',
      //   popover: {
      //     title: 'Guest List Management',
      //     description: 'View and manage all your invited guests in one place. Track RSVPs, meal preferences, and more.',
      //     side: 'left',
      //     align: 'start',
      //   },
      // },
      // {
      //   element: '[data-cy="guest-permissions"]',
      //   popover: {
      //     title: 'Set Guest Permissions',
      //     description: 'Control what each guest can see and do. You can give view-only access or allow them to help manage different aspects of your wedding.',
      //     side: 'left',
      //     align: 'start',
      //   },
      // },
      // {
      //   element: '[data-cy="invite-partner-btn"]',
      //   popover: {
      //     title: 'Invite Your Partner',
      //     description: 'Let’s invite your partner to help you plan! Collaborate on guest lists, tasks, and more. You can do this later if you prefer.',
      //     side: 'right',
      //     align: 'start',
      //   },
      // },
      // {
      //   element: '[data-cy="rsvp-tracking"]',
      //   popover: {
      //     title: 'Track RSVPs',
      //     description: 'Easily see who has responded to your invitations and manage guest counts for your wedding events.',
      //     side: 'top',
      //     align: 'start',
      //   },
      // },
    ],
  },
};

export const TutorialProvider = ({ children }) => {
  const driverRef = useRef(null);
  const activeTutorial = useRef(null);

  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);
  const startTutorial = useCallback((tutorialId, customSteps = null) => {
    // Cypress E2E clicks the real UI; driver.js overlays intercept those clicks.
    if (typeof window !== 'undefined' && window.Cypress) {
      return;
    }

      // Check if tutorial is already viewed
    const viewedTutorials = JSON.parse(localStorage.getItem('viewedTutorials') || '[]');
    if (viewedTutorials.includes(tutorialId)) {
      // console.log(`Tutorial ${tutorialId} already viewed, skipping...`);
      return;
    }

    const tutorial = TUTORIALS[tutorialId];
    if (!tutorial && !customSteps) return;

    // Destroy existing driver instance if any
    if (driverRef.current) {
      driverRef.current.destroy();
    }

    const steps = customSteps || (tutorial?.steps || []);
    if (steps.length === 0) return;

    driverRef.current = driver({
      showProgress: true,
      doneBtnText: 'Got it!',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      onDestroyed: async () => {
        // Mark tutorial as viewed when completed or closed
        if (tutorialId) {
          try {
            await fetch(`${process.env.REACT_APP_REQUEST_URL}/api/v1/tutorials/viewed`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ tutorialId })
            });
          } catch (error) {
            console.error('Error marking tutorial as viewed:', error);
            // Silently fail - don't interrupt user experience for this
          }
        }
        activeTutorial.current = null;
      },
    });

    driverRef.current.setSteps(steps);
    driverRef.current.drive();
    activeTutorial.current = tutorialId;
  }, []);

  const stopTutorial = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
      activeTutorial.current = null;
    }
  }, []);

  const isTutorialActive = useCallback((tutorialId = null) => {
    if (tutorialId === null) return activeTutorial.current !== null;
    return activeTutorial.current === tutorialId;
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        startTutorial,
        stopTutorial,
        isTutorialActive,
        TUTORIAL_IDS: Object.keys(TUTORIALS),
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};
